





































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";
import {
  BButton,
  BIconChevronLeft,
  BIconChevronRight,
  BOverlay,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import Calendar from "@/components/Calendar.vue";
import { Course, CourseSection } from "@/typings";
import CourseCard from "@/components/CourseCard.vue";
import { EventAttributes, createEvents, DateArray } from "ics";
import { saveAs } from "file-saver";
import { shortSemToLongSem } from "@/utilities";
import { Timeslot } from "@/typings";

Vue.directive("b-tooltip", VBTooltip);

function mod(n: number, m: number) {
  return ((n % m) + m) % m;
}

@Component({
  computed: {
    ...mapGetters(["departmentsInitialized", "catalogInitialized"]),
    ...mapGetters("schedule", ["numSchedules"]),
    ...mapState("schedule", ["lastNewSchedule", "currentCourseSet"]),
    shortSemToLongSem,
  },
  components: {
    Calendar,
    CourseCard,
    "b-button": BButton,
    "b-icon-chevron-left": BIconChevronLeft,
    "b-icon-chevron-right": BIconChevronRight,
    "b-overlay": BOverlay,
    "b-spinner": BSpinner,
  },
})
export default class Schedule extends Vue {
  keepSelected: Course[] = [];
  keepSelectedCourseSet = "";
  currentScheduleNumber = 0;
  currentSchedule: CourseSection[] = [];

  mounted(): void {
    this.getSchedule(this.currentScheduleNumber);
  }

  get selectedCourses(): Course[] {
    if (this.currentCourseSet !== this.keepSelectedCourseSet) {
      this.keepSelected = [];
      this.keepSelectedCourseSet = this.currentCourseSet;
    }
    if (this.keepSelected.length > 0) {
      return this.keepSelected;
    }

    for (const dept of this.$store.state.departments) {
      for (const course of dept.courses) {
        for (const section of course.sections) {
          if (this.$store.getters["schedule/isSelected"](section.crn)) {
            this.keepSelected.push(course);
            break;
          }
        }
      }
    }

    return this.keepSelected;
  }

  get filteredKeepSelected(): Course[] {
    return this.keepSelected.filter((course) =>
      course.sections.some((sec) =>
        this.$store.getters["schedule/isSelected"](sec.crn)
      )
    );
  }

  get lastNewSchedule(): number[] {
    return this.$store.state.schedule.lastNewSchedule;
  }

  get currentCourseSet(): string {
    return this.$store.state.schedule.currentCourseSet;
  }

  get visibleCurrentScheduleNumber(): number {
    // @ts-expect-error: This is mapped in the @Component decorator
    if (this.numSchedules === 0) {
      this.currentScheduleNumber = 0;
      return 0;
    }
    return this.currentScheduleNumber + 1;
  }

  get sectionsWithoutTimes(): CourseSection[] {
    const sections = [];

    for (const dept of this.$store.state.departments) {
      for (const course of dept.courses) {
        for (const sectionIdx in course.sections) {
          const section = course.sections[sectionIdx];

          if (
            this.$store.getters["schedule/isSelected"](section.crn) &&
            section.timeslots.every(
              (timeslot: Timeslot) => timeslot.days.length === 0
            ) &&
            // Only give sections with available seats (or the last one, if all are full)
            // It's very unfortunate that we need to do a string parse here
            (section.rem > 0 ||
              Number.parseInt(sectionIdx) === course.sections.length - 1)
          ) {
            sections.push(section);
            break;
          }
        }
      }
    }

    return sections;
  }

  async getSchedule(idx: number): Promise<void> {
    // @ts-expect-error: This is mapped in the @Component decorator
    if (this.numSchedules === 0) {
      this.currentSchedule = this.sectionsWithoutTimes;
      return;
    }

    let newSchedule = await this.$store.dispatch("schedule/getSchedule", idx);
    newSchedule.push(...this.sectionsWithoutTimes);

    this.currentSchedule = newSchedule;
  }

  /////////////////////
  // CHANGE SCHEDULE //
  /////////////////////

  @Watch("lastNewSchedule")
  reloadSchedules(): void {
    this.currentScheduleNumber = 0;
    this.getSchedule(this.currentScheduleNumber);
  }

  incrementSchedule(): void {
    this.currentScheduleNumber = mod(
      this.currentScheduleNumber + 1,
      // @ts-expect-error: This is mapped in the @Component decorator
      this.numSchedules
    );
    this.getSchedule(this.currentScheduleNumber);
  }

  decrementSchedule(): void {
    this.currentScheduleNumber = mod(
      this.currentScheduleNumber - 1,
      // @ts-expect-error: This is mapped in the @Component decorator
      this.numSchedules
    );
    this.getSchedule(this.currentScheduleNumber);
  }

  ////////////////////
  // OUTPUT TO USER //
  ////////////////////

  copyToClipboard(val: string): void {
    const tempInput = document.createElement("input");
    // @ts-expect-error: This works so ts is just being dumb
    tempInput.style = "position: absolute; left: -1000px; top: -1000px";
    tempInput.value = val;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);

    const copyIndicator = document.getElementById("crn-copy-indicator");
    // @ts-expect-error: I know it might be null but the element exists so stop complaining
    copyIndicator.className = "show";
    setTimeout(function () {
      // @ts-expect-error: I know it might be null but the element exists so stop complaining
      copyIndicator.className = copyIndicator.className.replace("show", "");
    }, 2000);
  }

  exportIcs(): void {
    const recurrenceDays: { [day: string]: string } = {
      U: "SU",
      M: "MO",
      T: "TU",
      W: "WE",
      R: "TH",
      F: "FR",
      S: "SA",
    };

    const dayNumToLetter: string[] = ["U", "M", "T", "W", "R", "F", "S"];

    // @ts-expect-error: shortSemToLongSem is defined in the computed section
    const year = this.shortSemToLongSem(process.env.VUE_APP_CURR_SEM).slice(-4);
    const events: EventAttributes[] = [];

    for (const section of this.currentSchedule) {
      //Generate recurrenceRule for the days in the timeslot
      for (const timeslot of section.timeslots) {
        //If the timeslot is not set, just skip it
        if (
          timeslot.days.length === 0 ||
          timeslot.timeStart < 0 ||
          timeslot.timeEnd < 0
        ) {
          continue;
        }

        const startMonth = timeslot.dateStart.split("/")[0];
        const [endMonth, endDay] = timeslot.dateEnd.split("/");

        // If the end month is before the start, increment the year. This handles cross-year sessions assuming they don't wrap more than once
        let effectiveEndDate = new Date(
          `${endMonth < startMonth ? year + 1 : year}/${endMonth}/${endDay}`
        );
        // Add one day to include the last day of the semester
        effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);

        let recurrenceRule = "FREQ=WEEKLY;BYDAY=";
        for (let i = 0; i < timeslot.days.length; i++) {
          if (i) {
            recurrenceRule += ",";
          }
          recurrenceRule += recurrenceDays[timeslot.days[i]];
        }
        recurrenceRule += `;INTERVAL=1;UNTIL=${effectiveEndDate
          .toISOString()
          .split("T")[0]
          .replaceAll("-", "")}`;

        // Make a js dates for start time
        const monthStart = timeslot.dateStart.split("/")[0];
        const dayStart = timeslot.dateStart.split("/")[1];
        const hourStart = Math.floor(timeslot.timeStart / 100);
        const minStart = timeslot.timeStart % 100;
        let startJSDate = new Date(
          `${year}/${monthStart}/${dayStart} ${hourStart}:${minStart}:0`
        );

        // Make a js dates for end time
        const monthEnd = timeslot.dateStart.split("/")[0];
        const dayEnd = timeslot.dateStart.split("/")[1];
        const hourEnd = Math.floor(timeslot.timeEnd / 100);
        const minEnd = timeslot.timeEnd % 100;
        let endJSDate = new Date(
          `${year}/${monthEnd}/${dayEnd} ${hourEnd}:${minEnd}:0`
        );

        // Find the first day after the semester starts that has this section
        // For example if the semster starts on a monday, but the section is on wednesday
        // then move the date up to the next wednesday
        while (!timeslot.days.includes(dayNumToLetter[startJSDate.getDay()])) {
          startJSDate.setDate(startJSDate.getDate() + 1);
          endJSDate.setDate(endJSDate.getDate() + 1);
        }

        const dateToArray = (date: Date): DateArray => {
          return [
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
          ];
        };
        // TODO convert this to using a config file where you can set the default timezone
        events.push({
          title: section.title,
          start: dateToArray(startJSDate),
          startInputType: "local",
          startOutputType: "local",
          end: dateToArray(endJSDate),
          endInputType: "local",
          endOutputType: "local",
          location: timeslot.location !== "TBA" ? timeslot.location : undefined,
          recurrenceRule,
        });
      }
    }

    //creates ics calendar
    const { error, value } = createEvents(events);

    if (!value) {
      // eslint-disable-next-line
      console.log(error);
      // eslint-disable-next-line
      alert(
        "There was an error generating your ics file. Please report this bug to the developers using the Discord or GitHub links in the website footer."
      );
    } else {
      // Add appropriate timezone region information into DTSTART/DTEND per RFC 5545
      // NOTE: Some calendar applications may not like that we aren't providing the actual timezone data, but this appears to work on Google Calendar and the iOS Calendar
      const TZID = "America/New_York"; // TODO: make TZID configurable
      const blob = new Blob(
        [
          value
            .replaceAll("DTSTART", `DTSTART;TZID=${TZID}`)
            .replaceAll("DTEND", `DTEND;TZID=${TZID}`),
        ],
        { type: "text/plain;charset=utf-8" }
      );
      saveAs(
        blob,
        `${this.currentCourseSet.toLowerCase().replaceAll(" ", "_")}.ics`
      );
    }
  }
}
